'use client';

import { c as _c } from "react/compiler-runtime";
import { Typography } from '@mui/material';
import { Microsoft365RestoreWorkload, RestoreJobType, type InventoryResource, type Microsoft365User, type Path, type RestoreJob } from '@repo/api-gw-sdk';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { BasicLink } from '@/components/explorer/fileLink';
import { scopeLabels, type Microsoft365UserRestoreScope } from '@/components/restore/microsoft365UserRestoreScope';
import { useDAL } from '@/contexts/useDAL';
import type { FilterProperty } from '@/types/advanceFilter';
import { isWholeUserItem, microsoft365DisplayPath, microsoft365ExplorerHref, microsoft365RestoreSourceLabel, parseMicrosoft365RestoreItem, type Microsoft365RestoreItem } from './microsoft365RestorePath';
import { RestoreValueLines } from './restoreValueLines';

// The job view names a user restore's coverage the way the wizard offered it: the whole user, or one workload.
const restoreScope = (workloads: Microsoft365RestoreWorkload[]): Microsoft365UserRestoreScope | undefined => {
  const hasOneDrive = workloads.includes(Microsoft365RestoreWorkload.MICROSOFT_365_RESTORE_WORKLOAD_ONEDRIVE);
  const hasExchange = workloads.includes(Microsoft365RestoreWorkload.MICROSOFT_365_RESTORE_WORKLOAD_MAILBOX);
  if (hasOneDrive && hasExchange) {
    return 'wholeUser';
  }
  if (hasOneDrive) {
    return 'onedrive';
  }
  if (hasExchange) {
    return 'exchange';
  }
  return undefined;
};
const microsoft365RestoreItems = (job: RestoreJob): Microsoft365RestoreItem[] => {
  if (job.restoreType !== RestoreJobType.Microsoft365Restore) {
    return [];
  }
  return (job.partialRestoreData?.files?.files ?? []).map(file => parseMicrosoft365RestoreItem(file.path, file.pathType)).filter((item): item is Microsoft365RestoreItem => !!item);
};
const userLabel = (user: Microsoft365User): string | undefined => user.displayName || user.userPrincipalName || user.mail;

/**
 * Microsoft 365 index rows carry no owner name, so users are named through the tenant's live
 * directory instead, matched on the id the job recorded. The job may address a user by object id or
 * by principal name, so both key the result — lowercase, since a recorded id's casing may differ
 * from the directory's. A user deleted since the restore keeps its raw id.
 */
const useMicrosoft365UserNames = (job: RestoreJob, userIds: (string | undefined)[]): Map<string, string> => {
  const dal = useDAL();
  const ids = [...new Set(userIds.filter((id): id is string => !!id))].sort();
  const {
    data
  } = dal.saasInventory.microsoft365.useUsersByIds(job.resourceDetails?.id ?? '', ids);
  const names = new Map<string, string>();
  for (const user of data?.users ?? []) {
    const label = userLabel(user);
    if (!label) {
      continue;
    }
    for (const key of [user.userId, user.userPrincipalName]) {
      if (key) {
        names.set(key.toLowerCase(), label);
      }
    }
  }
  return names;
};
const userNameOf = (names: Map<string, string>, userId: string | undefined): string | undefined => userId ? names.get(userId.toLowerCase()) : undefined;

/**
 * The named paths the rows show, for the copy control — copying the index form would hand back the
 * object ids the panel deliberately resolves away.
 */
export const useMicrosoft365RestorePathsToCopy = (job, files) => {
  const items = files.map(_temp);
  const names = useMicrosoft365UserNames(job, items.map(_temp2));
  return files.map((file_0, index) => microsoft365DisplayPath(file_0.path, userNameOf(names, items[index]?.userId))).join("\n");
};

/** Links the restored item into the Microsoft 365 explorer, labelled by its display path. */
export const Microsoft365RestorePathLink = t0 => {
  const $ = _c(11);
  const {
    job,
    resource,
    file
  } = t0;
  const item = parseMicrosoft365RestoreItem(file.path, file.pathType);
  const names = useMicrosoft365UserNames(job, [item?.userId]);
  const T0 = CopyToClipboardWrapper;
  const t1 = file.path;
  const T1 = BasicLink;
  const t2 = microsoft365DisplayPath(file.path, userNameOf(names, item?.userId));
  let t3;
  if ($[0] !== resource.id) {
    t3 = microsoft365ExplorerHref(resource.id);
    $[0] = resource.id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== T1 || $[3] !== resource || $[4] !== t2 || $[5] !== t3) {
    t4 = <T1 entity={resource} label={t2} href={t3} />;
    $[2] = T1;
    $[3] = resource;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== T0 || $[8] !== file.path || $[9] !== t4) {
    t5 = <T0 textToCopy={t1}>{t4}</T0>;
    $[7] = T0;
    $[8] = file.path;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const microsoft365RestoreSource: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreSource',
  displayName: 'Restored from',
  group: '',
  field: {
    Component: ({
      entity
    }) => {
      const sourceByEntity = new Map(microsoft365RestoreItems(entity).map(item => [item.entityId, item]));
      const items = [...sourceByEntity.values()];
      const names = useMicrosoft365UserNames(entity, items.map(item => item.userId));
      const labels = items.map(item => microsoft365RestoreSourceLabel(item, userNameOf(names, item.userId)));
      // useFields keeps a row whose Component returned an element, so an empty value has to resolve to
      // null here rather than inside RestoreValueLines.
      if (!labels.length) {
        return null;
      }
      return <RestoreValueLines lines={labels.map((label, index) => <Typography key={`${index}-${label}`}>{label}</Typography>)} textToCopy={labels.join('\n')} />;
    }
  }
};
export const microsoft365RestoreItem: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreItem',
  displayName: 'Restored item',
  group: '',
  field: {
    Component: ({
      entity
    }) => {
      // A job from before the workloads were reported carries no coverage either way, so only an
      // explicit `false` reads as partial.
      const isPartialUser = entity.partialRestoreData?.microsoft365User?.everyWorkload === false;
      const items = microsoft365RestoreItems(entity);
      const labels = items.map(item => isWholeUserItem(item) && isPartialUser ? 'Partial user' : item.item);
      // A multi-user job records one user root per user and they all read the same, so one line
      // stands for all of them; named items keep every occurrence.
      const lines = labels.filter((label, index) => !isWholeUserItem(items[index]) || labels.indexOf(label) === index);
      if (!lines.length) {
        return null;
      }
      return <RestoreValueLines lines={lines.map((label, index) => <Typography key={`${index}-${label}`}>{label}</Typography>)} textToCopy={lines.join('\n')} />;
    }
  }
};
export const microsoft365RestoreScope: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreScope',
  displayName: 'Scope',
  group: '',
  field: {
    // Only a user restore records workloads, so on every other Microsoft 365 restore the row is absent.
    Component: ({
      entity
    }) => {
      const scope = restoreScope(entity.partialRestoreData?.microsoft365User?.workloads ?? []);
      return !!scope && <Typography>{scopeLabels[scope]}</Typography>;
    }
  }
};
function _temp(file) {
  return parseMicrosoft365RestoreItem(file.path, file.pathType);
}
function _temp2(item) {
  return item?.userId;
}